import { apiRequest } from '../../api/client';
import type { KasPromoCodeEntry } from '../../components/kasPromoCode';

const unwrap = <T>(promise: Promise<{ data: T }>): Promise<T> => promise.then((e) => e.data);

/** Bốn mẫu đang chạy trên Admin Web. `ManualVoucher` có trong enum BE nhưng ẩn (tồn đọng). */
export type CampaignKind = 'Welcome' | 'Birthday' | 'FlashPoints' | 'WinBack';

export type CampaignState = 'Draft' | 'Active' | 'Paused' | 'Ended';

export type Weekday =
  | 'Sunday'
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday';

/** `TimeOnly` trên dây: `HH:mm:ss` — cùng convention `catalog/api.ts`. */
export interface CampaignConfig {
  voucherAmount?: number | null;
  voucherValidDays?: number | null;
  minBillAmount?: number | null;
  minGuestCount?: number | null;
  inactiveDays?: number | null;
  pointsMultiplier?: number | null;
  daysOfWeek?: Weekday[] | null;
  fromTime?: string | null;
  toTime?: string | null;
  sendAtTime?: string | null;

  /**
   * Đường gửi **thứ hai**. `null` = chỉ in-app + FCM.
   *
   * 🔴 In-app và FCM **luôn có**, như mọi app thông thường (PL chốt 2026-08-30). Ô này là *gửi thêm*,
   * không phải *chọn kênh*. ⚠️ `Sms` và `Zns` chưa có bộ gửi (chờ `S8-BE-04`) nên form khóa lại.
   */
  secondaryChannel?: SecondaryChannel | null;

  allowStacking: boolean;
}

/** Kênh gửi thứ hai — chỉ hai giá trị này; `Push`/`Inbox` đã nằm ở kênh chính. */
export type SecondaryChannel = 'Sms' | 'Zns';

/**
 * Lựa chọn cho ô "Gửi thêm qua". `disabled` vì **chưa có bộ gửi** — không phải vì phân quyền.
 * Bỏ `disabled` mà chưa có sender thì admin chọn xong hệ thống im lặng không gửi.
 *
 * Zalo ZNS mở khoá từ `S8-BE-10`: bộ gửi đã có, và job kiểm hạn mức trước mỗi lô rồi mới gửi.
 * SMS vẫn khoá — công việc nền mới chỉ có bộ gửi push và ZNS, chưa có bộ gửi SMS cho thông báo
 * (bộ gửi SMS hiện có chỉ phục vụ OTP, đi đường khác hẳn).
 */
export const SECONDARY_CHANNEL_OPTIONS: {
  value: SecondaryChannel;
  label: string;
  disabled: boolean;
  reason: string;
}[] = [
  {
    value: 'Sms',
    label: 'SMS',
    disabled: true,
    reason: 'Chưa có bộ gửi SMS — NotificationDispatchJob mới chỉ có IPushSender.',
  },
  {
    value: 'Zns',
    label: 'Zalo ZNS',
    disabled: false,
    reason: '',
  },
];

export interface Campaign {
  id: string;
  kind: CampaignKind;
  chainId: string | null;
  name: string;
  description: string | null;
  state: CampaignState;
  startsAt: string;
  endsAt: string | null;
  config: CampaignConfig;
  pointsBudget: number | null;
  maxTotalValue: number | null;
  maxRecipients: number | null;
  pointsSpent: number;
  totalValueIssued: number;
  recipientCount: number;
  pointsRemaining: number | null;
  valueRemaining: number | null;
  recipientsRemaining: number | null;
  isRunning: boolean;
  isLowBudget: boolean;
  missingFields: string[];
  pausedReason: string | null;
  pausedAt: string | null;
  /** Mã khuyến mãi KAS theo chuỗi. Rỗng = chưa khai chuỗi nào. */
  kasPromoCodes: KasPromoCodeEntry[] | null;
  createdAt: string;
}

export interface UpsertCampaignPayload {
  kind: CampaignKind;
  chainId?: string | null;
  /**
   * Mã KAS theo chuỗi. Bỏ trường này đi nghĩa là **không đụng** tới mã đã khai; gửi mảng
   * nghĩa là thay thế trọn bộ. Form luôn gửi mảng để đổi phạm vi kéo được mã cũ đi cùng.
   */
  kasPromoCodes?: { chainId: string; code: string }[];
  name: string;
  description?: string | null;
  startsAt: string;
  endsAt?: string | null;
  pointsBudget?: number | null;
  maxTotalValue?: number | null;
  maxRecipients?: number | null;
  config: CampaignConfig;
}

export interface CampaignRun {
  runId: string;
  campaignId: string;
  isDryRun: boolean;
  matchedCount: number;
  issuedCount: number;
  skippedCount: number;
  pointsIssued: number;
  totalValueIssued: number;
  stoppedBecause: string | null;
}

export const campaignKeys = {
  all: ['campaigns'] as const,
  list: (filters: { kind?: CampaignKind; state?: CampaignState; includeEnded: boolean }) =>
    [...campaignKeys.all, filters] as const,
};

export const KIND_LABELS: Record<CampaignKind, string> = {
  Welcome: 'Chào mừng',
  Birthday: 'Sinh nhật',
  FlashPoints: 'Hệ số điểm',
  WinBack: 'Winback',
};

export const KIND_OPTIONS = (Object.entries(KIND_LABELS) as [CampaignKind, string][]).map(
  ([value, label]) => ({ value, label }),
);

export const STATE_LABELS: Record<CampaignState, string> = {
  Draft: 'Bản nháp',
  Active: 'Đang chạy',
  Paused: 'Tạm dừng',
  Ended: 'Đã kết thúc',
};

export const WEEKDAY_OPTIONS: { value: Weekday; label: string }[] = [
  { value: 'Monday', label: 'Thứ 2' },
  { value: 'Tuesday', label: 'Thứ 3' },
  { value: 'Wednesday', label: 'Thứ 4' },
  { value: 'Thursday', label: 'Thứ 5' },
  { value: 'Friday', label: 'Thứ 6' },
  { value: 'Saturday', label: 'Thứ 7' },
  { value: 'Sunday', label: 'Chủ nhật' },
];

const WEEKDAY_SHORT: Record<Weekday, string> = {
  Monday: 'T2',
  Tuesday: 'T3',
  Wednesday: 'T4',
  Thursday: 'T5',
  Friday: 'T6',
  Saturday: 'T7',
  Sunday: 'CN',
};

export function isVoucherKind(kind: CampaignKind): boolean {
  return kind === 'Welcome' || kind === 'Birthday' || kind === 'WinBack';
}

export function emptyConfig(): CampaignConfig {
  return { allowStacking: false, sendAtTime: '08:00:00' };
}

export function formatClock(value: string | null | undefined): string {
  if (!value) return '';
  return value.slice(0, 5);
}

export function audienceLabel(campaign: Campaign): string {
  switch (campaign.kind) {
    case 'Welcome':
      return 'Mọi khách vừa đăng ký';
    case 'Birthday':
      return 'Khách có sinh nhật trong tháng';
    case 'FlashPoints':
      return 'Toàn bộ thành viên';
    case 'WinBack': {
      const days = campaign.config.inactiveDays;
      return days != null ? `Khách ${days} ngày chưa quay lại` : 'Khách lâu không quay lại';
    }
  }
}

export function scheduleLabel(campaign: Campaign): string {
  const cfg = campaign.config;
  switch (campaign.kind) {
    case 'Welcome':
      return 'Liên tục';
    case 'Birthday':
    case 'WinBack':
      return cfg.sendAtTime ? `Mỗi ngày ${formatClock(cfg.sendAtTime)}` : 'Mỗi ngày';
    case 'FlashPoints': {
      const days =
        cfg.daysOfWeek && cfg.daysOfWeek.length > 0
          ? cfg.daysOfWeek.map((d) => WEEKDAY_SHORT[d]).join(', ')
          : 'Mọi ngày';
      if (cfg.fromTime && cfg.toTime) {
        return `${days} · ${formatClock(cfg.fromTime)}–${formatClock(cfg.toTime)}`;
      }
      return days;
    }
  }
}

const VND = new Intl.NumberFormat('vi-VN');

export function formatMoney(amount: number): string {
  if (amount >= 1_000_000) {
    const millions = amount / 1_000_000;
    const rounded = millions >= 10 ? millions.toFixed(0) : millions.toFixed(1).replace(/\.0$/, '');
    return `${rounded.replace('.', ',')} triệu`;
  }
  return `${VND.format(amount)}đ`;
}

export function formatPoints(amount: number): string {
  return `${VND.format(amount)} điểm`;
}

/** Cột ngân sách — đọc số còn lại từ server, không tự trừ. */
export function budgetLabel(campaign: Campaign): { text: string; unlimited: boolean } {
  const parts: string[] = [];
  if (campaign.pointsRemaining != null) parts.push(`còn ${formatPoints(campaign.pointsRemaining)}`);
  if (campaign.valueRemaining != null) parts.push(`còn ${formatMoney(campaign.valueRemaining)}`);
  if (campaign.recipientsRemaining != null) {
    parts.push(`còn ${VND.format(campaign.recipientsRemaining)} khách`);
  }
  if (parts.length === 0) return { text: 'không giới hạn', unlimited: true };
  return { text: parts.join(' · '), unlimited: false };
}

export function statusLabel(campaign: Campaign): { label: string; tone: 'success' | 'warning' | 'default' } {
  if (campaign.state === 'Draft') return { label: 'Bản nháp', tone: 'default' };
  if (campaign.state === 'Ended') return { label: 'Đã kết thúc', tone: 'default' };
  if (campaign.state === 'Paused') return { label: 'Tạm dừng', tone: 'warning' };
  if (campaign.isRunning) return { label: 'Đang chạy', tone: 'success' };
  return { label: 'Hết hiệu lực', tone: 'warning' };
}

export function dryRunSummary(run: CampaignRun): string {
  return `${VND.format(run.matchedCount)} khách · ${formatMoney(run.totalValueIssued)}`;
}

function listQuery(filters: { kind?: CampaignKind; state?: CampaignState; includeEnded: boolean }): string {
  const params = new URLSearchParams();
  if (filters.kind) params.set('kind', filters.kind);
  if (filters.state) params.set('state', filters.state);
  if (filters.includeEnded) params.set('includeEnded', 'true');
  const q = params.toString();
  return q ? `?${q}` : '';
}

export const campaignApi = {
  list: (filters: { kind?: CampaignKind; state?: CampaignState; includeEnded: boolean }) =>
    unwrap(apiRequest<Campaign[]>(`/admin/campaigns${listQuery(filters)}`)),

  create: (body: UpsertCampaignPayload) =>
    unwrap(apiRequest<Campaign>('/admin/campaigns', { method: 'POST', body })),

  update: (id: string, body: UpsertCampaignPayload) =>
    unwrap(apiRequest<Campaign>(`/admin/campaigns/${id}`, { method: 'PUT', body })),

  activate: (id: string) =>
    unwrap(apiRequest<Campaign>(`/admin/campaigns/${id}/activate`, { method: 'POST' })),

  pause: (id: string, reason?: string) =>
    unwrap(apiRequest<Campaign>(`/admin/campaigns/${id}/pause`, { method: 'POST', body: { reason } })),

  end: (id: string) =>
    unwrap(apiRequest<Campaign>(`/admin/campaigns/${id}/end`, { method: 'POST' })),

  dryRun: (id: string) =>
    unwrap(apiRequest<CampaignRun>(`/admin/campaigns/${id}/dry-run`, { method: 'POST' })),

  run: (id: string) =>
    unwrap(apiRequest<CampaignRun>(`/admin/campaigns/${id}/run`, { method: 'POST' })),
};
